import { useState, useEffect, useMemo } from "react";
import { useInvoice } from "../../stores/useInvoice";
import { StyledDashboardBarChart } from "./dashBoardBarChart.styled";
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
  LineController,
  BarController,
} from "chart.js";
import { Bar, Chart } from "react-chartjs-2";
import { useTheme } from "@mui/material";

ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  LineController,
  BarController
);

type DashBoardBarChartProps = {
  incomePerMonth: number[];
  goodsPerMonth: number[];
  variousPerMonth: number[];
  payoutPerMonth: number[];
  profitPerMonth: number[];
  currentYear: string;
};

type ChartProps = {
  incomePerMonth: number[];
  goodsPerMonth: number[];
  variousPerMonth: number[];
  payoutPerMonth: number[];
  profitPerMonth: number[];
};

export const DashBoardBarChart = ({
  incomePerMonth,
  goodsPerMonth,
  variousPerMonth,
  currentYear,
  payoutPerMonth,
  profitPerMonth,
}: DashBoardBarChartProps) => {
  const [labels, setLabels] = useState<string[]>([]);
  const [chartData, setChartData] = useState<ChartProps | undefined>();
  const theme = useTheme();

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: "Inkomen/Uitgaven/Varia per maand",
      },
    },
    barThickness: 30,
  };

  //get labels
  useEffect(() => {
    const year = Number(currentYear);
    const fullYearLabels = Array.from({ length: 12 }, (_, i) => {
      const month = String(i + 1).padStart(2, "0");
      return `${year}-${month}`;
    });

    setLabels(fullYearLabels);
  }, [currentYear]);

  useEffect(() => {
    setChartData({
      incomePerMonth: incomePerMonth,
      goodsPerMonth: goodsPerMonth,
      variousPerMonth: variousPerMonth,
      payoutPerMonth: payoutPerMonth,
      profitPerMonth: profitPerMonth,
    });
  }, [
    incomePerMonth,
    goodsPerMonth,
    variousPerMonth,
    payoutPerMonth,
    profitPerMonth,
  ]);

  const data = useMemo(() => {
    if (!labels.length || !chartData) return null;

    return {
      labels,
      datasets: [
        {
          type: "bar" as const,
          label: "Inkomen",
          data: chartData.incomePerMonth,
          backgroundColor: theme.palette.success.main,
          stack: "Stack 1",
          order: 1,
        },
        {
          type: "bar" as const,
          label: "Uitbetalingen",
          data: chartData.payoutPerMonth,
          backgroundColor: theme.palette.warning.main,
          stack: "Stack 2",
          order: 1,
        },
        {
          type: "bar" as const,
          label: "Goederen",
          data: chartData.goodsPerMonth.map((n) => -Math.abs(n)),
          backgroundColor: theme.palette.error.main,
          stack: "Stack 1",
          order: 1,
        },
        {
          type: "bar" as const,
          label: "Diversen",
          data: chartData.variousPerMonth.map((n) => -Math.abs(n)),
          backgroundColor: theme.palette.info.main,
          stack: "Stack 1",
          order: 1,
        },
        {
          type: "line" as const,
          label: "Winst",
          data: chartData.profitPerMonth,
          borderColor: theme.color.main,
          backgroundColor: theme.color.dark,
          borderWidth: 3,
          pointRadius: 4,

          order: 0, // 👈 LOWER than bars → drawn LAST → on top
        },
      ],
    };
  }, [labels, chartData]);

  return (
    <StyledDashboardBarChart>
      {data ? (
        <Chart type="bar" options={options} data={data} />
      ) : (
        <p>Loading chart...</p>
      )}
    </StyledDashboardBarChart>
  );
};
